import { CallDto } from '@act/shared/data-transfer-objects';
import { useMemo } from 'react';
import { callsAppSelectors } from 'redux/slices/callsSlice.slice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { isConnectionActive } from '@act/shared/utilities';
import {
  dedupeCallConnections,
  isActiveCallConnection,
} from './activeCallConnection.util';

/**
 * Selects and derives connection lists for the active call menu.
 *
 * @param initialCall Call from props (fallback when Redux has not hydrated).
 * @returns Connection-derived state for ActiveCallMenu UI.
 */
export function useActiveCallConnections(initialCall: CallDto) {
  const call =
    callsAppSelectors.useSelectCallById(initialCall?.id) || initialCall;

  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const allConnections = call?.connections || [];

  const connections = useMemo(() => {
    const filteredConnections = actingUser?.id
      ? allConnections.filter(connection => connection.userId !== actingUser.id)
      : allConnections;

    return dedupeCallConnections(filteredConnections);
  }, [allConnections, actingUser?.id]);

  const guideConnection = useMemo(() => {
    if (!actingUser?.id) {
      return null;
    }
    return allConnections.find(
      connection =>
        connection.userId === actingUser.id &&
        isConnectionActive(connection)
    );
  }, [allConnections, actingUser?.id]);

  const guideHasAnswered = !!guideConnection?.connectedAt;
  const isMemberKnown = !!call?.subjectId;
  const mainConnection = call.incoming ? call.originator : call.receiver;
  const isMainConnectionOnHold = mainConnection?.onHold ?? false;

  const activeConnectionsCount = useMemo(
    () => allConnections.filter(isActiveCallConnection).length,
    [allConnections]
  );

  const isResourceCall =
    !!call?.resourceInteraction || !!call?.needResourceInteraction;

  const shouldShowAddCallerButton =
    activeConnectionsCount < 3 && !isResourceCall;

  return {
    call,
    actingUser,
    allConnections,
    connections,
    guideConnection,
    guideHasAnswered,
    isMemberKnown,
    mainConnection,
    isMainConnectionOnHold,
    activeConnectionsCount,
    isResourceCall,
    shouldShowAddCallerButton,
  };
}
